import { useEffect, useState, useRef, useCallback, memo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Slim progress bar at the top of the viewport during route transitions.
 * Mimics NProgress behavior: fast start → slow crawl → instant finish.
 *
 * Since this app uses React.lazy (not data loaders), `useNavigation()` won't
 * fire. Instead we detect Suspense fallback durations: when the location
 * changes we start the bar, then finish it after a short delay or when the
 * next paint settles (whichever is later).
 */
export const TopLoader = memo(function TopLoader() {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const prevPathRef = useRef(location.pathname);
  const isFirstMount = useRef(true);

  const cleanup = useCallback(() => {
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
  }, []);

  const finish = useCallback(() => {
    cleanup();
    setProgress(100);
    timeoutRef.current = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 300);
  }, [cleanup]);

  useEffect(() => {
    // Skip the initial mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevPathRef.current = location.pathname;
      return;
    }

    // Only trigger on actual path changes (not search/hash changes)
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;

    // Start the progress bar
    cleanup();
    setProgress(0);
    setVisible(true);

    // Quick jump to ~30%, then slow crawl
    timeoutRef.current = setTimeout(() => setProgress(30), 50);

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        const increment = (90 - prev) * 0.04;
        return Math.min(prev + Math.max(increment, 0.5), 90);
      });
    }, 200);

    // Finish after the lazy component likely loaded (idle callback or timeout)
    if ('requestIdleCallback' in window) {
      const idleId = requestIdleCallback(() => finish(), { timeout: 2000 });
      return () => {
        cancelIdleCallback(idleId);
        cleanup();
      };
    } else {
      const t = setTimeout(() => finish(), 400);
      return () => {
        clearTimeout(t);
        cleanup();
      };
    }
  }, [location.pathname, cleanup, finish]);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-[200] h-[2.5px]" role="progressbar" aria-valuenow={Math.round(progress)}>
      <div
        className="h-full bg-primary transition-all duration-200 ease-out"
        style={{ width: `${progress}%` }}
      >
        {/* Glow effect on the leading edge */}
        <div className="absolute right-0 top-0 h-full w-24 -translate-x-1 bg-gradient-to-l from-primary/60 to-transparent" />
      </div>
    </div>
  );
});
